/**
 * The degraded mapping editor — ADR-135 D5 (owner ruling 2026-08-10), S8.
 *
 * Binding a data set to an EXTERNAL source without writing JSON: the sibling of `SchemaBuilder`,
 * reached by the same `＋ New` gesture, differing in what the data document holds. A stored set's
 * holds records; this one holds the CONTRACT and no values (ADR-131 D2 / S8 E3).
 *
 * THE FIELD PICKER (ADR-135 4a, landed 2026-08-11)
 * -----------------------------------------------
 * This form shipped first with the entity and the field TYPED, because the resolve endpoint did
 * not exist and the alternative was that nothing for mapping landed until it did. Both pickers
 * are now live and the two degradation banners are gone. What remains from that period, and is
 * worth keeping, is the shape of the fallback: every picker here degrades to the typed row rather
 * than to a dead form, so a provider outage costs an operator convenience and not the ability to
 * author (ADR-121 D1).
 *
 * WHAT THE CASCADE IS, AND WHY IT IS NOT "PICK A PROJECT, THEN PICK A FIELD"
 * ------------------------------------------------------------------------
 * A discovery tier declares what it is SCOPED BY (S1), and 4a binds that scope from the caller's
 * args because at author time there is no set to bind an instance from. The scope is not uniform:
 * Asana's `task.fields` needs `project_gid` `from: instance`, so the instance tier is resolved and
 * offered as a pick-list; HubSpot's `crm_object.fields` needs `object_type` `from: enum`, so a
 * closed vocabulary is offered and NO instance is involved at all. So this renders one control per
 * declared parameter and chooses the control from `from` — a hardcoded two-step would be wrong for
 * HubSpot, and a hardcoded parameter name would be a second copy of the manifest (ADR-129 D1).
 *
 * THE PICKED INSTANCE ID IS NOT WRITTEN DOWN
 * -----------------------------------------
 * Picking an instance yields a name AND an id. The name is what the contract binds (`instance:
 * {name}`), which is the whole point of ADR-131's by-name rule. The id is used to scope the field
 * resolve and then discarded — it is deliberately NOT persisted as `resolved_id`, because S8 E8
 * requires `resolved_id` to travel with a `resolved_at` stamp written by RESOLUTION. An id stored
 * from a picker is a hardcoded GID wearing a cache's name, and the validator refuses it.
 *
 * WHAT IT REFUSES TO DECIDE — same three as `SchemaBuilder`, plus one
 * ------------------------------------------------------------------
 * Validity is the canonical Python validator's answer (staged over the real registry), ownership
 * has no picker, the layer-2 vocabulary is closed — and here, additionally, **the instance id is
 * not an input.** See above: resolution stamps `resolved_id` and `resolved_at` together (S8 E8).
 */

import { useEffect, useRef, useState } from 'react'

import {
  api, dataFindingLine, type DataDraftVerdict, type ResolveKeysVerdict, type ResolvedDataKey,
} from '../api/client'
import { serializeDocument } from './dataDocumentEdit'
// The two registry-NAME derivations. Imported from the stored-set module rather than copied,
// because a set's name is a property of the registry and not of which form authored it — two
// copies would let the two forms propose differently-named siblings (ADR-129 D1).
import { collectionFor, pluralise } from './schemaDraft'
import { useGuardedSpec } from './useGuardedSpec'
import {
  classOf, derivationMode, entitiesFor, fieldScopeFor, generateSourceDraft, hasInstanceTier,
  schemaTypeFor, sourceProblems,
  type MappedField, type SourceDraftSpec, type SourceProvider, type SourceScopeParam,
} from './sourceDraft'

const TYPES: MappedField['type'][] = ['string', 'number', 'integer', 'boolean']

const EMPTY: SourceDraftSpec = {
  name: '', setName: '', version: 1, description: '',
  provider: '', entity: '', instanceName: '',
  collection: '', key: 'key', maxItems: 500, ttlMinutes: 60,
  fields: [{ name: 'key', from: '', type: 'string', required: true }],
}

export interface SourceBuilderProps {
  canPropose: boolean
  onClose: () => void
  onCreated: (setRef: string) => void
}

export function SourceBuilder({ canPropose, onClose, onCreated }: SourceBuilderProps) {
  // The verdict AND the paths it was computed for, as one value written in one go, plus the run
  // it belongs to — see `SchemaBuilder.tsx` for the full reasoning. Short version: the findings
  // split asks whose file a finding is about, and only the STAGED paths can answer that; an edit
  // landing between request and response would otherwise leave the operator's own draft filed
  // under the registry heading, and a response the form has moved past would repopulate a verdict
  // the edit deliberately cleared.
  const [checked, setChecked] = useState<{ verdict: DataDraftVerdict; paths: string[] } | null>(null)
  const verdict = checked?.verdict ?? null
  const checkRun = useRef(0)
  const [unchecked, setUnchecked] = useState<string | null>(null)
  // A verdict about a draft the operator has since edited is a stale verdict, and a stale pass
  // is the dangerous kind — so any edit invalidates it, INCLUDING one still in flight.
  const clearVerdict = () => {
    checkRun.current += 1
    setChecked(null)
    setUnchecked(null)
  }
  // The draft, and the ONLY two ways to change it — there is no raw setter in this scope on
  // purpose. See `useGuardedSpec`, and the two controls in `SchemaBuilder` that proved why the
  // rule above cannot be a call each edit site has to remember.
  const { spec, updateSpec, patch } = useGuardedSpec<SourceDraftSpec>(EMPTY, clearVerdict)
  // Which DERIVED names the operator has taken over — see SchemaBuilder for why this is tracked
  // rather than inferred from the value.
  const [ownedNames, setOwnedNames] = useState<{ setName: boolean; collection: boolean }>({
    setName: false, collection: false,
  })
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [providers, setProviders] = useState<SourceProvider[] | null>(null)
  const [providerNote, setProviderNote] = useState<string | null>(null)
  const [managementData, setManagementData] = useState<boolean | null>(null)
  // The field tier's scope, BOUND. Author-time only: 4a binds a tier's scope from the caller's
  // args because there is no set yet to bind an instance from. None of this is persisted — see
  // the module note on why a picked id must not become `resolved_id`.
  //
  // THE ONE STATE HERE THAT DELIBERATELY DOES NOT CLEAR THE VERDICT. It is not in the spec and
  // `generateSourceDraft` never reads it, so binding a scope changes which field list the picker
  // offers and changes nothing the validator was asked about. Clearing on it would throw away a
  // pass for a draft that did not move. Picking an INSTANCE is different — that writes
  // `instanceName` into the draft, so `pickInstance` goes through `patch` and does clear.
  const [scopeArgs, setScopeArgs] = useState<Record<string, string>>({})
  const [instanceKeys, setInstanceKeys] = useState<ResolveKeysVerdict | null>(null)
  const [instanceBusy, setInstanceBusy] = useState(false)
  const [fieldKeys, setFieldKeys] = useState<ResolveKeysVerdict | null>(null)
  const [fieldsBusy, setFieldsBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  const [proposing, setProposing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A registration that WARNED is terminal but not a failure. The changeset is committed and its
  // pull request is open, so the builder must not close as though the set were on its way to
  // review, and it must not offer the control again -- a second propose opens a second pull
  // request rather than retrying the first.
  const [registrationWarning, setRegistrationWarning] = useState<string | null>(null)

  useEffect(() => {
    api.getDataSourceProviders()
      .then(res => {
        // An OMITTED list is "could not look", not "this platform integrates nothing" — the
        // endpoint sends no `providers` key in that case, so the distinction survives the wire.
        if (res.providers === undefined) {
          setProviders(null)
          setProviderNote(res.note ?? 'the provider list could not be read')
        } else {
          setProviders(res.providers)
          setManagementData(res.management_data ?? false)
        }
      })
      .catch(err => setProviderNote(err instanceof Error ? err.message : String(err)))
  }, [])

  const problems = sourceProblems(spec)
  const wellFormed = problems.length === 0
  const draft = wellFormed ? generateSourceDraft(spec) : null
  const mode = derivationMode(spec.provider, spec.entity, providers)
  const isMCP = classOf(spec.provider) === 'mcp'
  // Declared entities for the chosen provider. Non-empty means the entity is PICKED; empty
  // means it is typed, and the banner below says which of the two reasons applies.
  const declaredEntities = entitiesFor(spec.provider, providers)
  // What the field tier needs supplied, read from the declaration rather than assumed. `from`
  // chooses each control; see the module note on why this is not a fixed two-step.
  const scopeParams = fieldScopeFor(spec.provider, spec.entity, providers)
  const needsInstance = scopeParams.some(p => p.from === 'instance')
  const instanceTier = hasInstanceTier(spec.provider, spec.entity, providers)
  const scopeBound = scopeParams.every(p => (scopeArgs[p.name] ?? '') !== '')
  // The bound scope as a stable string, so the field resolve is keyed on the VALUES rather than
  // on the object's identity: re-binding a parameter to the same id must not re-fetch. Hoisted
  // out of the dependency array so the linter can check it statically — an expression inline
  // there is one nothing verifies.
  const scopeKey = JSON.stringify(scopeArgs)
  const canPickFields = mode === 'introspected' && scopeBound
  // Whether every mapped row came from the picker. `from` is the provider's own reported id, so
  // a row whose `from` is not in the resolved list was typed — including one typed before a
  // resolve succeeded, which is why this is computed rather than tracked as a flag.
  const resolvedIds = fieldKeys?.outcome === 'ok' ? fieldKeys.keys.map(k => k.id) : []
  const pickedEveryField = spec.fields.length > 0
    && spec.fields.every(f => resolvedIds.includes(f.from))

  // A resolve is scoped to one provider+entity, so a stale answer from the previous pair would
  // offer fields that belong to something else.
  useEffect(() => {
    setScopeArgs({})
    setInstanceKeys(null)
    setFieldKeys(null)
  }, [spec.provider, spec.entity])

  useEffect(() => {
    if (!needsInstance || !instanceTier) return
    let live = true
    setInstanceBusy(true)
    api.resolveDataKeys({ provider: spec.provider, entity: spec.entity, tier: 'instance' })
      .then(v => { if (live) setInstanceKeys(v) })
      // A rejected promise is the ONE case the endpoint does not cover (the network under it),
      // and it is still "we could not look" — never an empty list.
      .catch(err => {
        if (live) {
          setInstanceKeys({ outcome: 'unavailable',
            reason: err instanceof Error ? err.message : String(err) })
        }
      })
      .finally(() => { if (live) setInstanceBusy(false) })
    return () => { live = false }
  }, [spec.provider, spec.entity, needsInstance, instanceTier])

  useEffect(() => {
    if (!canPickFields) return
    let live = true
    setFieldsBusy(true)
    api.resolveDataKeys({
      provider: spec.provider, entity: spec.entity, tier: 'fields', args: scopeArgs,
    })
      .then(v => { if (live) setFieldKeys(v) })
      .catch(err => {
        if (live) {
          setFieldKeys({ outcome: 'unavailable',
            reason: err instanceof Error ? err.message : String(err) })
        }
      })
      .finally(() => { if (live) setFieldsBusy(false) })
    return () => { live = false }
    // `scopeKey`, not `scopeArgs`: see its definition. The object is read inside the effect but
    // deliberately not depended on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.provider, spec.entity, canPickFields, scopeKey])

  /** The same derivation as the stored-set form beside this one. Two sibling forms reached by one
   *  gesture must not differ in how many names they ask for. */
  const patchSchemaName = (name: string) => {
    const next: Partial<SourceDraftSpec> = { name }
    if (!ownedNames.setName) {
      const setName = pluralise(name)
      next.setName = setName
      if (!ownedNames.collection) next.collection = collectionFor(setName)
    }
    patch(next)
  }

  const patchSetName = (setName: string) => {
    setOwnedNames(o => ({ ...o, setName: true }))
    patch(ownedNames.collection ? { setName } : { setName, collection: collectionFor(setName) })
  }

  const patchField = (i: number, next: Partial<MappedField>) => {
    updateSpec(s => ({ ...s, fields: s.fields.map((f, j) => (j === i ? { ...f, ...next } : f)) }))
  }

  /** Picking an instance yields BOTH halves and they go to different places: the name is the
   *  contract's binding (ADR-131's administer-by-name rule), the id scopes the field resolve and
   *  is then discarded. Storing the id would need a `resolved_at` this form cannot honestly write
   *  (S8 E8), which is why it lives in component state and nowhere else. */
  const pickInstance = (param: SourceScopeParam, key: ResolvedDataKey) => {
    setScopeArgs(a => ({ ...a, [param.name]: key.id }))
    patch({ instanceName: key.name })
  }

  /** Adding a picked field. `from` is the provider's own path exactly as the provider reported it
   *  — not reconstructed here, because a path this form assembled could differ from the one the
   *  resolver will read. The row stays editable afterwards: a picked field is a starting point,
   *  not a lock. */
  const addPickedField = (key: ResolvedDataKey) => {
    const mapped = schemaTypeFor(key.type)
    if ('refuse' in mapped) return
    const name = key.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    const fields = [
      // A first row still on its untouched default is REPLACED rather than appended to, so the
      // common path (open the form, pick a field) does not leave an empty `key` row behind.
      ...spec.fields.filter((f, i) => !(i === 0 && f.name === 'key' && f.from === '')),
      { name: name || key.id, from: key.id, type: mapped.type, required: false },
    ]
    // Replacing that row can orphan the key SELECTION, which names a field by name. Left alone it
    // points at a field that no longer exists — and the draft is then refused for a reason the
    // operator never chose, at a control they never touched.
    const keyStillExists = fields.some(f => f.name === spec.key)
    patch({ fields, key: keyStillExists ? spec.key : fields[0].name })
  }

  const check = async () => {
    if (!draft) return
    // The paths this request is ABOUT, read before the await so the verdict can be stored with
    // them, and the run they belong to, so a response the form has moved past can be recognised.
    const paths = [draft.schemaPath, draft.dataPath]
    checkRun.current += 1
    const run = checkRun.current
    setChecking(true)
    setError(null)
    try {
      const v = await api.validateDataDraft({
        schema_path: draft.schemaPath, data_path: draft.dataPath,
        schema: draft.schema, data: draft.data,
      })
      // The draft changed while this was out. The answer describes something the operator is no
      // longer looking at, and the edit cleared the verdict on purpose — so it is dropped.
      if (run !== checkRun.current) return
      if (v.findings === undefined) {
        // The validator could not run. NOT "the draft is valid".
        setChecked(null)
        setUnchecked(v.error ?? 'the validator returned no findings array')
      } else {
        setChecked({ verdict: v, paths })
        setUnchecked(null)
      }
    } catch (err) {
      if (run !== checkRun.current) return
      setChecked(null)
      setUnchecked(err instanceof Error ? err.message : String(err))
    } finally {
      setChecking(false)
    }
  }

  const propose = async () => {
    if (!draft) return
    setProposing(true)
    setError(null)
    try {
      const res = await api.proposeFleetChangeset(
        [
          { path: draft.schemaPath, content: serializeDocument(draft.schema) },
          { path: draft.dataPath, content: serializeDocument(draft.data) },
        ],
        `data(${spec.setName}@v${spec.version}): bind a new set to ${spec.provider} ` +
        // The mode and the PROVENANCE of the mapping, because a reviewer reading only the PR
        // needs to know whether the field paths came from the provider or from a person.
        `via the Data area — ${mode}, fields ${pickedEveryField ? 'picked from' : 'asserted against'}` +
        ` ${spec.provider}`)
      if (res.improvement_warning) {
        setRegistrationWarning(res.improvement_warning)
        return
      }
      onCreated(`${spec.setName}@v${spec.version}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setProposing(false)
    }
  }

  // Proposing requires a verdict that says valid — a published version is IMMUTABLE, so this is
  // the last moment the answer is cheap.
  const canCreate = canPropose && wellFormed && verdict?.valid === true && !proposing
    && registrationWarning === null

  // Split by whose file the finding is about — see `SchemaBuilder.tsx` for the full reasoning.
  // The endpoint validates this fleet's whole staged registry, so a file the operator never
  // touched can refuse their draft; saying "the validator rejects this draft" about someone
  // else's file is the routeless dead end ADR-127 D1 designs against.
  const draftPaths = checked?.paths ?? []
  const findingLines = (verdict?.findings ?? []).flatMap((f, i) =>
    (f.errors ?? []).map((e, j) => ({ key: `${i}-${j}`, rel: f.rel ?? '', text: dataFindingLine(e) })))
  const draftFindings = findingLines.filter(l => draftPaths.includes(l.rel))
  const registryFindings = findingLines.filter(l => !draftPaths.includes(l.rel))

  return (
    <div className="border border-gray-200 rounded p-4" data-testid="source-builder">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900">New external source</h2>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:underline">
          Cancel
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-3" data-testid="sb2-what">
        The values stay in the integration — this repo stores only the contract: which provider,
        which fields we read, and how fresh a read must be. Nothing here copies the data in.
      </p>

      {providerNote && (
        <p className="text-sm text-amber-800 mb-3" data-testid="sb2-provider-note">
          {providerNote}. <strong>This is not “no integrations exist”</strong> — the catalog could
          not be read, so no provider list is being claimed.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="text-xs text-gray-700">
          Provider
          <select
            value={spec.provider}
            onChange={e => patch({ provider: e.target.value })}
            disabled={!providers}
            data-testid="sb2-provider"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          >
            <option value="">— choose —</option>
            {(providers ?? []).map(p => (
              <option key={p.provider} value={p.provider}>{p.provider}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-700">
          Entity {!isMCP
            ? <span className="text-gray-400">(not used by an http: provider)</span>
            : declaredEntities.length > 0
              ? <span className="text-gray-400">(declared by the platform catalog)</span>
              : <span className="text-gray-400">(typed — see below)</span>}
          {isMCP && declaredEntities.length > 0 ? (
            /* S1 landed for this provider, so the entity is PICKED. The mode beside each
               option is the operator-facing half of S8 E6: it says, before anything is
               created, whether this set will be checked against the provider or hand-mapped. */
            <select
              value={spec.entity}
              onChange={e => patch({ entity: e.target.value })}
              data-testid="sb2-entity-select"
              className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
            >
              <option value="">— choose —</option>
              {declaredEntities.map(e => (
                <option key={e.entity} value={e.entity}>
                  {e.label} {e.fields ? '· checkable' : '· hand-mapped'}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={spec.entity}
              onChange={e => patch({ entity: e.target.value })}
              disabled={!isMCP}
              placeholder="section"
              data-testid="sb2-entity"
              className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono
                         disabled:bg-gray-50"
            />
          )}
        </label>
        <label className="text-xs text-gray-700">
          Schema name
          <input
            value={spec.name}
            onChange={e => patchSchemaName(e.target.value)}
            placeholder="asana-triage-section"
            data-testid="sb2-name"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="text-xs text-gray-700">
          Set name{' '}
          <span className="text-gray-400">(the data file, and the default reference alias)</span>
          <input
            value={spec.setName}
            onChange={e => patchSetName(e.target.value)}
            placeholder="asana-triage-sections"
            data-testid="sb2-setname"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          />
          <span className="text-[10px] text-gray-400">
            Follows the schema name until you change it.
          </span>
        </label>
        <label className="text-xs text-gray-700">
          Instance name <span className="text-gray-400">(by NAME — never an id)</span>
          <input
            value={spec.instanceName}
            onChange={e => patch({ instanceName: e.target.value })}
            disabled={!isMCP}
            placeholder="Support Triage"
            data-testid="sb2-instance"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm
                       disabled:bg-gray-50"
          />
        </label>
        <label className="text-xs text-gray-700">
          Key field <span className="text-gray-400">(the stable name consumers spell)</span>
          <select
            value={spec.key}
            onChange={e => patch({ key: e.target.value })}
            data-testid="sb2-key"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          >
            {spec.fields.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-700">
          Max records <span className="text-gray-400">(the runtime cap — no ceiling)</span>
          <input
            type="number" min={1} value={spec.maxItems}
            onChange={e => patch({ maxItems: Number(e.target.value) })}
            data-testid="sb2-max"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-gray-700">
          Freshness TTL <span className="text-gray-400">(minutes)</span>
          <input
            type="number" min={1} value={spec.ttlMinutes}
            onChange={e => patch({ ttlMinutes: Number(e.target.value) })}
            data-testid="sb2-ttl"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-gray-700 col-span-2">
          Description <span className="text-gray-400">(what a reader needs to know)</span>
          <input
            value={spec.description}
            onChange={e => patch({ description: e.target.value })}
            data-testid="sb2-description"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          />
        </label>
      </div>

      {/* ADVANCED — the input that has a correct answer already. The collection property appears
          in NO reference (a token reads `by_key`, never the property name) and every reader
          discovers it by lookup, so it is derived and kept visible rather than asked for. */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          data-testid="sb2-advanced-toggle"
          aria-expanded={advancedOpen}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          {advancedOpen ? '▾' : '▸'} Advanced
          {!advancedOpen && <span className="text-gray-400"> — collection property</span>}
        </button>
        {advancedOpen && (
          <div className="grid grid-cols-2 gap-3 mt-2" data-testid="sb2-advanced">
            <label className="text-xs text-gray-700">
              Collection property
              <input
                value={spec.collection}
                onChange={e => {
                  setOwnedNames(o => ({ ...o, collection: true }))
                  patch({ collection: e.target.value })
                }}
                placeholder="sections"
                data-testid="sb2-collection"
                className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
              />
              <span className="text-[10px] text-gray-400">
                The array’s property name inside the schema. Nothing references it by name.
              </span>
            </label>
          </div>
        )}
      </div>

      {/* THE ENTITY TIER — three states, deliberately not merged. Whether the entity is picked
          and whether the SET is checkable are different questions with different remedies, and
          a single "degraded" banner would answer neither. */}
      {isMCP && declaredEntities.length === 0 && (
        <div className="mb-3 text-sm text-amber-800" data-testid="sb2-degraded-entity">
          The entity is typed too, for a different reason:{' '}
          {managementData === false
            ? 'the platform catalog does not declare entities for any provider yet'
            : `the catalog declares entities, but none for ${spec.provider || 'this provider'}`}
          , so there is nothing to choose from. That also makes this set{' '}
          <strong>hand-mapped (asserted)</strong> — it will not be checked against the provider
          on a schedule, and it must not be read as verified. When the catalog declares this
          entity, the set becomes checkable with no edit to the files below.
        </div>
      )}
      {isMCP && spec.entity && mode === 'asserted' && declaredEntities.length > 0 && (
        /* Declared, and deliberately without a field tier. NOT our gap: the provider describes
           no shape for this entity, which S1 F4 makes a first-class outcome rather than an
           error — Asana's `section` is the shipped example. */
        <div className="mb-3 text-sm text-amber-800" data-testid="sb2-entity-asserted">
          <strong>Hand-mapped (asserted).</strong> The catalog declares this entity but the
          provider describes no field shape for it, so there is nothing to check a mapping
          against — now or later. Map it against a sample and treat it as your assertion; it
          will not be verified against the provider on a schedule.
        </div>
      )}
      {isMCP && spec.entity && mode === 'introspected' && (
        <div className="mb-3 text-sm text-green-700" data-testid="sb2-entity-introspected">
          <strong>Checkable.</strong> The provider describes this entity’s fields, so the
          mapping will be re-derived and compared on a schedule, and a drift raises a change
          rather than going unnoticed — and it is what makes the field list below a picker
          rather than something to type.
        </div>
      )}

      {/* THE FIELD PICKER (ADR-135 4a). One control per DECLARED scope parameter, then the
          resolved field list. Every branch here degrades to the typed row below rather than to a
          dead form: a provider outage costs convenience, not the ability to author. */}
      {mode === 'introspected' && (
        <div className="mb-4 border border-gray-200 rounded p-3" data-testid="sb2-picker">
          {scopeParams.map(param => (
            <div key={param.name} className="mb-2" data-testid={`sb2-scope-${param.name}`}>
              <label className="text-xs text-gray-700">
                {param.name}{' '}
                <span className="text-gray-400">
                  {param.from === 'instance'
                    ? '(which instance the fields belong to)'
                    : param.from === 'enum'
                      ? '(one of the provider’s declared values)'
                      : `(from: ${param.from})`}
                </span>
                {param.from === 'enum' ? (
                  <select
                    value={scopeArgs[param.name] ?? ''}
                    onChange={e => setScopeArgs(a => ({ ...a, [param.name]: e.target.value }))}
                    data-testid={`sb2-scope-enum-${param.name}`}
                    className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
                  >
                    <option value="">Choose…</option>
                    {(param.values ?? []).map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                ) : param.from === 'instance' && instanceTier ? (
                  <>
                    {instanceBusy && (
                      <p className="text-xs text-gray-500 mt-1" data-testid="sb2-instance-busy">
                        Asking the provider which instances exist…
                      </p>
                    )}
                    {instanceKeys?.outcome === 'ok' && (
                      <select
                        value={scopeArgs[param.name] ?? ''}
                        onChange={e => {
                          const picked = instanceKeys.keys.find(k => k.id === e.target.value)
                          if (picked) pickInstance(param, picked)
                        }}
                        data-testid={`sb2-scope-instance-${param.name}`}
                        className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
                      >
                        <option value="">Choose…</option>
                        {instanceKeys.keys.map(k => (
                          <option key={k.id} value={k.id}>{k.name}</option>
                        ))}
                      </select>
                    )}
                    {instanceKeys && instanceKeys.outcome !== 'ok' && (
                      /* Named, never rendered as an empty list — "we could not look" and "there
                         are none" are different sentences with different remedies. */
                      <p className="text-sm text-amber-800 mt-1"
                         data-testid={`sb2-instance-${instanceKeys.outcome}`}>
                        {instanceKeys.outcome === 'unauthorized'
                          ? 'The provider rejected our credential, so the instance list could ' +
                            'not be read — an operator has to fix the token, not retry: '
                          : instanceKeys.outcome === 'unsupported'
                            ? 'This provider does not list instances: '
                            : 'The instance list could not be read just now: '}
                        {instanceKeys.reason} <strong>This is not “there are none.”</strong> Type
                        the instance name above and map the fields by hand below.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      value={scopeArgs[param.name] ?? ''}
                      onChange={e => setScopeArgs(a => ({ ...a, [param.name]: e.target.value }))}
                      data-testid={`sb2-scope-typed-${param.name}`}
                      className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm
                                 font-mono"
                    />
                    <span className="text-xs text-amber-800">
                      No pick-list for this one: it is scoped <code>from: {param.from}</code>
                      {param.from === 'instance' && ' and this entity declares no instance tier'}
                      , so the value is typed.
                    </span>
                  </>
                )}
              </label>
            </div>
          ))}

          {!scopeBound && scopeParams.length > 0 && (
            <p className="text-xs text-gray-500" data-testid="sb2-scope-incomplete">
              The provider describes fields per {scopeParams.map(p => p.name).join(' + ')}, so
              that comes first — the same field list differs between them.
            </p>
          )}
          {fieldsBusy && (
            <p className="text-xs text-gray-500" data-testid="sb2-fields-busy">
              Reading the field list…
            </p>
          )}
          {fieldKeys?.outcome === 'ok' && (
            <div data-testid="sb2-fields-list">
              <p className="text-xs text-gray-500 mb-1">
                {fieldKeys.keys.length} field{fieldKeys.keys.length === 1 ? '' : 's'} the provider
                describes right now. Adding one fills the row below; you can still edit it.
              </p>
              {fieldKeys.truncated && (
                <p className="text-sm text-amber-800 mb-1" data-testid="sb2-fields-truncated">
                  Only the first {fieldKeys.cap} are shown — the provider describes more. The rest
                  are not missing; add them by hand if you need one.
                </p>
              )}
              <ul className="text-sm">
                {fieldKeys.keys.map(k => {
                  const mapped = schemaTypeFor(k.type)
                  const already = spec.fields.some(f => f.from === k.id)
                  return (
                    <li key={k.id} className="flex items-center gap-2 py-0.5"
                        data-testid={`sb2-field-key-${k.id}`}>
                      <button
                        type="button"
                        onClick={() => addPickedField(k)}
                        disabled={'refuse' in mapped || already}
                        data-testid={`sb2-field-add-${k.id}`}
                        className="text-xs text-indigo-700 hover:underline disabled:text-gray-300
                                   disabled:no-underline"
                      >
                        {already ? 'added' : 'add'}
                      </button>
                      <span>{k.name}</span>
                      <code className="text-xs text-gray-400">{k.id}</code>
                      {'refuse' in mapped ? (
                        /* Pickable-but-refused with the reason, rather than mapped to `string`
                           and quietly wrong. The hand-typed row is still the escape hatch. */
                        <span className="text-xs text-amber-800">— {mapped.refuse}</span>
                      ) : (
                        <span className="text-xs text-gray-400">{mapped.type}</span>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {fieldKeys && fieldKeys.outcome !== 'ok' && (
            <p className="text-sm text-amber-800" data-testid={`sb2-fields-${fieldKeys.outcome}`}>
              {fieldKeys.outcome === 'unauthorized'
                ? 'The provider rejected our credential, so its field list could not be read: '
                : fieldKeys.outcome === 'unsupported'
                  ? 'This provider does not describe this entity’s fields: '
                  : 'The field list could not be read just now: '}
              {fieldKeys.reason} <strong>This is not “it has no fields.”</strong> Map them by hand
              below — that is your assertion, and it is how this form worked before the picker.
            </p>
          )}
        </div>
      )}

      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
        Fields we read
      </h3>
      <table className="w-full text-sm mb-2">
        <tbody>
          {spec.fields.map((f, i) => (
            <tr key={i} data-testid={`sb2-field-${i}`}>
              <td className="pr-2">
                <input
                  value={f.name}
                  onChange={e => patchField(i, { name: e.target.value })}
                  placeholder="our field"
                  data-testid={`sb2-field-name-${i}`}
                  className="w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
                />
              </td>
              <td className="pr-2 text-gray-400">←</td>
              <td className="pr-2">
                <input
                  value={f.from}
                  onChange={e => patchField(i, { from: e.target.value })}
                  placeholder="their path"
                  data-testid={`sb2-field-from-${i}`}
                  className="w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
                />
              </td>
              <td className="pr-2">
                <select
                  value={f.type}
                  onChange={e => patchField(i, { type: e.target.value as MappedField['type'] })}
                  data-testid={`sb2-field-type-${i}`}
                  className="border border-gray-200 rounded px-2 py-1 text-sm"
                >
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </td>
              <td className="pr-2">
                <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                  <input
                    type="checkbox" checked={f.required}
                    onChange={e => patchField(i, { required: e.target.checked })}
                    data-testid={`sb2-field-required-${i}`}
                  />
                  required
                </label>
              </td>
              <td>
                {spec.fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => patch({ fields: spec.fields.filter((_, j) => j !== i) })}
                    data-testid={`sb2-field-remove-${i}`}
                    className="text-xs text-gray-400 hover:text-red-700"
                  >
                    remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => patch({
          fields: [...spec.fields, { name: '', from: '', type: 'string', required: false }] })}
        data-testid="sb2-add-field"
        className="text-xs text-indigo-700 hover:underline mb-4"
      >
        ＋ add field
      </button>

      {problems.length > 0 && (
        <ul className="text-sm text-amber-800 mb-3" data-testid="sb2-problems">
          {problems.map(p => <li key={p}>{p}</li>)}
        </ul>
      )}

      {draft && (
        <p className="text-xs text-gray-500 mb-3" data-testid="sb2-paths">
          <code>{draft.schemaPath}</code> + <code>{draft.dataPath}</code> — proposed together as
          one changeset. The second file holds the contract only: no records are written here,
          because the values are not ours to store.
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={check}
          disabled={!wellFormed || checking}
          data-testid="sb2-check"
          className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40"
        >
          {checking ? 'Checking…' : 'Check'}
        </button>
        <button
          type="button"
          onClick={propose}
          disabled={!canCreate}
          data-testid="sb2-create"
          className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-40"
        >
          {proposing ? 'Proposing…' : 'Create'}
        </button>
        {!canPropose && (
          <span className="text-xs text-gray-500">Creating needs the fleet-owner role.</span>
        )}
      </div>

      {unchecked && (
        <p className="mt-3 text-sm text-amber-800" data-testid="sb2-unchecked">
          The draft could not be validated: {unchecked}. <strong>This is not “the draft is
          valid”</strong> — creating is refused rather than guessed, because a published version
          is immutable.
        </p>
      )}
      {verdict?.valid === true && (
        <p className="mt-3 text-sm text-green-700" data-testid="sb2-valid">
          The canonical validator accepts this contract.
        </p>
      )}
      {verdict && verdict.valid !== true && (
        <div className="mt-3" data-testid="sb2-findings">
          {(draftFindings.length > 0 || verdict.compile_note) && (
            <>
              <p className="text-sm text-red-700">The canonical validator rejects this draft:</p>
              <ul className="text-xs text-red-700">
                {draftFindings.map(l => (
                  <li key={l.key}><code>{l.rel}</code> — {l.text}</li>
                ))}
                {verdict.compile_note && <li>{verdict.compile_note}</li>}
              </ul>
            </>
          )}
          {registryFindings.length > 0 && (
            <div className="mt-2" data-testid="sb2-registry-findings">
              <p className="text-sm text-red-700">
                This fleet’s data registry is already invalid in a file this draft does not touch.
                Nothing on this form caused it, and creating stays refused until that file is
                corrected:
              </p>
              <ul className="text-xs text-red-700">
                {registryFindings.map(l => (
                  <li key={l.key}><code>{l.rel}</code> — {l.text}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {registrationWarning && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid="sb2-registration-warning">
          <p>{registrationWarning}</p>
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-700" data-testid="sb2-error">{error}</p>}
    </div>
  )
}
